import { APP_NAME, OPERATOR, SUPPORT_CONTACT } from '@eventping/shared'
import { Link } from '@tanstack/react-router'
import { RadioTower, TriangleAlert } from 'lucide-react'
import type * as React from 'react'
import { useMe, useTexts } from '@/lib/api'
import { cn } from '@/lib/utils'

/** `compact`: below 420px wide, the mark alone (the name stays for screen readers). */
export function Wordmark({ className, compact }: { className?: string; compact?: boolean }) {
  return (
    <span className={cn('inline-flex items-center gap-2 font-semibold tracking-tight', className)}>
      <span className="grid size-8 place-items-center rounded-xl bg-primary text-primary-foreground">
        <RadioTower className="size-4.5" aria-hidden />
      </span>
      <span className={cn(compact && 'max-[419px]:sr-only')}>{APP_NAME}</span>
    </span>
  )
}

/** The page column: one comfortable width on every screen, safe-area aware. */
export function Page({
  children,
  header,
  className,
}: {
  children: React.ReactNode
  header?: React.ReactNode
  className?: string
}) {
  return (
    <div className="min-h-dvh pb-[max(2rem,env(safe-area-inset-bottom))]">
      {header && (
        <header className="sticky top-0 z-30 border-b bg-background/90 backdrop-blur supports-[backdrop-filter]:bg-background/75">
          <div className="mx-auto flex min-h-16 max-w-2xl items-center gap-3 px-4 pt-[env(safe-area-inset-top)]">
            {header}
          </div>
        </header>
      )}
      <main className={cn('mx-auto max-w-2xl px-4 pt-6', className)}>{children}</main>
    </div>
  )
}

/**
 * On every page: the brand, and the links carriers and reviewers look for. The tagline says
 * "text updates" only while texts are on, or on the opt-in proof pages (`texts`), which
 * always describe texting. It also names who operates the service (OPERATOR), as the
 * toll-free registration does.
 */
export function Footer({ note, texts }: { note?: React.ReactNode; texts?: boolean }) {
  const link = 'inline-flex min-h-10 items-center underline-offset-4 hover:underline'
  const support = useMe().data?.support ?? SUPPORT_CONTACT
  const textsOn = useTexts() || texts === true
  return (
    <footer className="mt-12 space-y-1 border-t pt-6 text-center text-sm text-muted-foreground">
      {note && <p className="mb-2">{note}</p>}
      <nav aria-label="Legal" className="flex flex-wrap justify-center gap-x-5">
        <Link to="/terms" className={link}>
          Terms
        </Link>
        <Link to="/privacy" className={link}>
          Privacy
        </Link>
        <Link to="/opt-in" className={link}>
          How opt-in works
        </Link>
        <Link to="/pricing" className={link}>
          Pricing
        </Link>
      </nav>
      <p>
        {APP_NAME} ·{' '}
        {textsOn ? 'Step-by-step text updates for any event' : 'Step-by-step updates for any event'}{' '}
        ·{' '}
        {support.includes('@') ? (
          <a href={`mailto:${support}`} className="underline-offset-4 hover:underline">
            {support}
          </a>
        ) : (
          support
        )}
      </p>
      <p>
        {APP_NAME} is operated by {OPERATOR}.
      </p>
    </footer>
  )
}

/**
 * For creators: says plainly when texting or email is switched off (SMS_PROVIDER /
 * EMAIL_PROVIDER=disabled), so nobody expects a text that will never go out. Sign-ups still
 * work and are recorded; they simply get no confirmation yet.
 */
export function ChannelsNotice({
  channels,
  textsEnabled,
}: {
  channels: { sms: boolean; email: boolean }
  /** Texts-off mode: texting is not part of the product, so only email is spoken of. */
  textsEnabled: boolean
}) {
  if (!textsEnabled) channels = { ...channels, sms: true }
  if (channels.sms && channels.email) return null
  const off =
    !channels.sms && !channels.email
      ? 'Texting and email are'
      : !channels.sms
        ? 'Texting is'
        : 'Email is'
  return (
    <div
      role="status"
      data-testid="channels-notice"
      className="mb-5 flex gap-3 rounded-2xl border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-50"
    >
      <TriangleAlert className="mt-0.5 size-4 shrink-0" aria-hidden />
      <p>
        <strong className="font-semibold">{off} not active yet.</strong> People can still join your
        events{textsEnabled && ' and their consent is recorded'}, but nothing is sent to them until
        it is turned on.
      </p>
    </div>
  )
}

export function Card({ className, ...props }: React.ComponentProps<'section'>) {
  return (
    <section className={cn('rounded-2xl border bg-card p-5 shadow-xs', className)} {...props} />
  )
}

export function SectionTitle({
  children,
  aside,
}: {
  children: React.ReactNode
  aside?: React.ReactNode
}) {
  return (
    <div className="mb-3 flex items-baseline justify-between gap-3">
      <h2 className="text-lg font-semibold">{children}</h2>
      {aside && <div className="text-sm text-muted-foreground">{aside}</div>}
    </div>
  )
}

export function FieldError({ children }: { children?: React.ReactNode }) {
  if (!children) return null
  return (
    <p role="alert" className="mt-1.5 text-sm text-destructive">
      {children}
    </p>
  )
}

export function timeOf(ms: number): string {
  const date = new Date(ms)
  const today = new Date()
  const sameDay = date.toDateString() === today.toDateString()
  const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  return sameDay
    ? time
    : `${date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}, ${time}`
}
